import to from "await-to-js";
import ky, { HTTPError, type Options as KyOptions } from "ky";

import { ApiError } from "../../error/api-error";
import { ApiEndpoints } from "../constants/api-endpoints";
import { authTokensResponseSchema } from "../validation/auth-schemas";
import { ErrorCodesEnum } from "~/common/api/constants/error-code";
import type { ApiResponse, ErrorResponse } from "~/common/api/types";
import { RoutePaths } from "~/common/constants/route-paths";
import { UrlParamsKeys } from "~/common/constants/url-params-keys";
import { ValidationApiError } from "~/common/error/validation-api-error";
import { dataStorage } from "~/common/services/data-storage-service";
import { Sentry } from "~/config/sentry";

interface SafeRequestOptions {
  body?: unknown;
  headers?: Record<string, string>;
  params?: Record<string, string | number | boolean>;
  auth?: {
    accessToken?: boolean;
    refreshToken?: boolean;
    token?: string;
  };
}

interface UnsafeRequestOptions extends SafeRequestOptions {
  body?: unknown;
}

type HttpMethod = "GET" | "POST" | "PATCH" | "PUT" | "DELETE";

// Endpoints that should not trigger token refresh on 401
const AUTH_ENDPOINTS: ApiEndpoints[] = [
  ApiEndpoints.REFRESH_TOKEN,
  ApiEndpoints.SIGNIN,
  ApiEndpoints.SIGNUP,
  ApiEndpoints.COMPANY_USERS_INVITE,
];

/**
 * HTTP API service for making requests to the backend
 * Provides typed methods for common HTTP operations with error handling
 */
export class ApiService {
  private readonly baseApiUrl: string;

  private readonly kyInstance: typeof ky;

  constructor() {
    this.baseApiUrl = import.meta.env.VITE_API_URL as string;
    this.kyInstance = ky.create({
      prefixUrl: this.baseApiUrl,
      retry: 0, // We handle retries manually for token refresh
      timeout: 30000,
    });
  }

  private onUnauthorized(): void {
    dataStorage.clearAuthTokens();

    const pathName = window.location.pathname;
    const normalizedPath = pathName.endsWith("/")
      ? pathName.slice(1)
      : pathName;

    if (
      !(<string[]>[
        RoutePaths.SIGN_IN,
        RoutePaths.SIGN_UP,
        RoutePaths.LOGIN_COMPANY_INVITED_NEW,
        RoutePaths.LOGIN_COMPANY_INVITED_EXISTING,
      ]).includes(normalizedPath)
    ) {
      const redirectPath = normalizedPath + window.location.search;

      window.location.href = `${RoutePaths.SIGN_IN}?${UrlParamsKeys.REDIRECT_TO}=${encodeURIComponent(redirectPath)}`;
    }
  }

  /**
   * Internal method for sending HTTP requests using ky
   * @param endpoint - API endpoint path
   * @param method - HTTP method
   * @param options - Request options including body, headers, and params
   * @returns Promise resolving to the response data
   * @throws ApiError when request fails
   */
  private async sendRequest<TResponse extends ApiResponse = ApiResponse>(
    endpoint: string,
    method: HttpMethod,
    options?: SafeRequestOptions,
    isRetryAfterRefresh = false,
  ): Promise<TResponse> {
    const { auth = {} } = options || {};

    // Build headers
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...options?.headers,
    };

    const authTokens = dataStorage.getAuthTokens();

    // If route requires Auth and we have no tokens - redirect user to Sign in page
    if ((auth.accessToken || auth.refreshToken) && !authTokens) {
      this.onUnauthorized();

      return null as unknown as TResponse;
    }

    // Set authorization header based on auth options
    if (auth.accessToken && authTokens) {
      headers.Authorization = `Bearer ${authTokens.accessToken}`;
    } else if (auth.refreshToken && authTokens) {
      headers.Authorization = `Bearer ${authTokens.refreshToken}`;
    } else if (auth.token) {
      headers.Authorization = `Bearer ${auth.token}`;
    }

    // Build ky options
    const kyOptions: KyOptions = {
      method,
      headers,
      searchParams: options?.params as Record<
        string,
        string | number | boolean
      >,
    };

    // Add JSON body for methods that support it
    if (options?.body && ["POST", "PATCH", "PUT"].includes(method)) {
      kyOptions.json = options.body;
    }

    // Remove leading slash from endpoint if present (ky prefixUrl handles this)
    const normalizedEndpoint = endpoint.startsWith("/")
      ? endpoint.slice(1)
      : endpoint;

    try {
      const response = await this.kyInstance(normalizedEndpoint, kyOptions);

      // Handle 204 No Content
      if (response.status === 204) {
        return null as unknown as TResponse;
      }

      const responsePayload = await response.json<TResponse>();

      // Check if API returned error status in payload
      if (responsePayload?.status === "Error") {
        const errorResponse = responsePayload as unknown as ErrorResponse;

        throw this.createApiError(
          errorResponse,
          response.status,
          response.statusText,
          `${this.baseApiUrl}/${normalizedEndpoint}`,
          method,
          options?.body,
        );
      }

      return responsePayload;
    } catch (error) {
      // Handle ky HTTPError
      if (error instanceof HTTPError) {
        return this.handleHttpError<TResponse>(
          error,
          endpoint,
          normalizedEndpoint,
          method,
          options,
          isRetryAfterRefresh,
        );
      }

      // Re-throw ApiError and ValidationApiError as-is
      if (error instanceof ApiError) {
        throw error;
      }

      // Handle unexpected errors
      Sentry.logger.error("Unexpected error in sendRequest", { error });
      Sentry.captureException(error);
      throw new Error("Не вдалось виконати запит до сервера");
    }
  }

  /**
   * Handles HTTP errors from ky, including token refresh logic
   */
  private async handleHttpError<TResponse extends ApiResponse>(
    error: HTTPError,
    endpoint: string,
    normalizedEndpoint: string,
    method: HttpMethod,
    options?: SafeRequestOptions,
    isRetryAfterRefresh = false,
  ): Promise<TResponse> {
    const { response } = error;
    const url = `${this.baseApiUrl}/${normalizedEndpoint}`;

    let responsePayload: ApiResponse | null = null;

    try {
      responsePayload = await response.json<ApiResponse>();
    } catch (error) {
      Sentry.logger.error("Error parsing error response payload");
      Sentry.captureException(error);
    }

    // Handle 401 Unauthorized with token refresh
    if (
      response.status === 401 &&
      !isRetryAfterRefresh &&
      !AUTH_ENDPOINTS.includes(endpoint as ApiEndpoints)
    ) {
      const authTokens = dataStorage.getAuthTokens();

      if (authTokens?.refreshToken) {
        const [refreshError, refreshResponse] = await to(
          this.sendPublicPatch(ApiEndpoints.REFRESH_TOKEN, {
            body: { refreshToken: authTokens.refreshToken },
          }),
        );

        if (refreshError || !refreshResponse) {
          this.onUnauthorized();

          return null as unknown as TResponse;
        }

        // Save new tokens and retry the original request
        const tokens = authTokensResponseSchema.parse(refreshResponse.data);

        dataStorage.putAuthTokens(tokens);

        return this.sendRequest<TResponse>(endpoint, method, options, true);
      }

      this.onUnauthorized();

      return null as unknown as TResponse;
    }

    // Create and throw appropriate error
    const errorResponse = responsePayload as ErrorResponse | null;

    throw this.createApiError(
      errorResponse,
      response.status,
      response.statusText,
      url,
      method,
      options?.body,
    );
  }

  /**
   * Creates an ApiError or ValidationApiError from response data
   */
  private createApiError(
    errorResponse: ErrorResponse | null,
    status: number,
    statusText: string,
    url: string,
    method: HttpMethod,
    requestPayload?: unknown,
  ): ApiError {
    const errorMessage =
      errorResponse?.errorMessage || `HTTP ${status}: ${statusText}`;
    const errorCode = errorResponse?.errorCode || ErrorCodesEnum.UNKNOWN;

    const errorOptions = {
      message: errorMessage,
      status,
      statusText,
      url,
      method,
      errorCode,
      responsePayload: errorResponse,
      requestPayload,
    };

    if (errorCode === ErrorCodesEnum.VALIDATION) {
      return new ValidationApiError(errorOptions);
    }

    return new ApiError(errorOptions);
  }

  /**
   * Sends a GET request to the specified endpoint
   * @param endpoint - API endpoint path
   * @param options - Request options (headers, query params)
   * @returns Promise resolving to the response data
   */
  async sendPublicGet<T = unknown>(
    endpoint: string,
    options?: SafeRequestOptions,
  ): Promise<ApiResponse<T>> {
    return this.sendRequest<ApiResponse<T>>(endpoint, "GET", options);
  }

  /**
   * Sends a POST request to the specified endpoint
   * @param endpoint - API endpoint path
   * @param options - Request options (body, headers, query params)
   * @returns Promise resolving to the response data
   */
  async sendPublicPost<T = unknown>(
    endpoint: string,
    options?: UnsafeRequestOptions,
  ): Promise<ApiResponse<T>> {
    return this.sendRequest<ApiResponse<T>>(endpoint, "POST", options);
  }

  /**
   * Sends a PATCH request to the specified endpoint
   * @param endpoint - API endpoint path
   * @param options - Request options (body, headers, query params)
   * @returns Promise resolving to the response data
   */
  async sendPublicPatch<T = unknown>(
    endpoint: string,
    options?: UnsafeRequestOptions,
  ): Promise<ApiResponse<T>> {
    return this.sendRequest<ApiResponse<T>>(endpoint, "PATCH", options);
  }

  /**
   * Sends a PUT request to the specified endpoint
   * @param endpoint - API endpoint path
   * @param options - Request options (body, headers, query params)
   * @returns Promise resolving to the response data
   */
  async sendPublicPut<T = unknown>(
    endpoint: string,
    options?: UnsafeRequestOptions,
  ): Promise<ApiResponse<T>> {
    return this.sendRequest<ApiResponse<T>>(endpoint, "PUT", options);
  }

  /**
   * Sends a DELETE request to the specified endpoint
   * @param endpoint - API endpoint path
   * @param options - Request options (headers, query params)
   * @returns Promise resolving to the response data
   */
  async sendPublicDelete<T = unknown>(
    endpoint: string,
    options?: SafeRequestOptions,
  ): Promise<ApiResponse<T>> {
    return this.sendRequest<ApiResponse<T>>(endpoint, "DELETE", options);
  }

  /**
   * Sends a GET request to the specified endpoint
   * @param endpoint - API endpoint path
   * @param options - Request options (headers, query params)
   * @returns Promise resolving to the response data
   */
  async sendGet<T = unknown>(
    endpoint: string,
    options?: SafeRequestOptions,
  ): Promise<ApiResponse<T>> {
    return this.sendPublicGet<T>(endpoint, {
      auth: { accessToken: true },
      ...options,
    });
  }

  /**
   * Sends a POST request to the specified endpoint
   * @param endpoint - API endpoint path
   * @param options - Request options (body, headers, query params)
   * @returns Promise resolving to the response data
   */
  async sendPost<T = unknown>(
    endpoint: string,
    options?: UnsafeRequestOptions,
  ): Promise<ApiResponse<T>> {
    return this.sendPublicPost<T>(endpoint, {
      auth: { accessToken: true },
      ...options,
    });
  }

  /**
   * Sends a PATCH request to the specified endpoint
   * @param endpoint - API endpoint path
   * @param options - Request options (body, headers, query params)
   * @returns Promise resolving to the response data
   */
  async sendPatch<T = unknown>(
    endpoint: string,
    options?: UnsafeRequestOptions,
  ): Promise<ApiResponse<T>> {
    return this.sendPublicPatch<T>(endpoint, {
      auth: { accessToken: true },
      ...options,
    });
  }

  /**
   * Sends a PUT request to the specified endpoint
   * @param endpoint - API endpoint path
   * @param options - Request options (body, headers, query params)
   * @returns Promise resolving to the response data
   */
  async sendPut<T = unknown>(
    endpoint: string,
    options?: UnsafeRequestOptions,
  ): Promise<ApiResponse<T>> {
    return this.sendPublicPut<T>(endpoint, {
      auth: { accessToken: true },
      ...options,
    });
  }

  /**
   * Sends a DELETE request to the specified endpoint
   * @param endpoint - API endpoint path
   * @param options - Request options (headers, query params)
   * @returns Promise resolving to the response data
   */
  async sendDelete<T = unknown>(
    endpoint: string,
    options?: SafeRequestOptions,
  ): Promise<ApiResponse<T>> {
    return this.sendPublicDelete<T>(endpoint, {
      auth: { accessToken: true },
      ...options,
    });
  }
}
